import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/apiClient";

export default function AccountPage() {
  const { user, logout, loading } = useAuth();
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [rentals, setRentals] = useState([]);

  useEffect(() => {
    if (user) {
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/rental/bookings").then((r) => setRentals(r.data)).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="py-24 text-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-12" data-testid="account-page">
      <div className="flex justify-between items-end mb-10">
        <div>
          <div className="overline text-[color:var(--vt-orange)] mb-2">Welcome back</div>
          <h1 className="font-display font-black text-5xl tracking-tighter">{user.name}</h1>
          <p className="text-neutral-500 text-sm">{user.email}</p>
        </div>
        <button onClick={logout} className="vt-btn-outline" data-testid="account-logout">{t.nav.logout}</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <section>
          <h2 className="font-display font-bold text-2xl mb-4">Orders</h2>
          {orders.length === 0 ? (
            <div className="border border-dashed border-neutral-300 p-8 text-center text-neutral-500 text-sm">No orders yet. <Link to="/shop" className="link-underline text-black">Shop now</Link></div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="border border-neutral-200 p-4" data-testid={`order-${o.order_number}`}>
                  <div className="flex justify-between mb-2">
                    <div className="font-bold">#{o.order_number}</div>
                    <div className="overline">{o.status}</div>
                  </div>
                  <div className="text-xs text-neutral-500 mb-2">{new Date(o.created_at).toLocaleDateString()}</div>
                  <div className="flex justify-between items-end">
                    <div className="text-sm">{o.items.length} item(s) · <span className="font-bold">€{o.total.toFixed(2)}</span></div>
                    {(o.status === "paid" || o.invoice_generated) && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/invoice`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs font-bold uppercase tracking-wider text-[color:var(--vt-orange)] link-underline"
                        data-testid={`invoice-${o.order_number}`}
                      >
                        Download Invoice ↓
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-display font-bold text-2xl mb-4">Rental Bookings</h2>
          {rentals.length === 0 ? (
            <div className="border border-dashed border-neutral-300 p-8 text-center text-neutral-500 text-sm">No rentals yet. <Link to="/rental" className="link-underline text-black">Rent a bike</Link></div>
          ) : (
            <div className="space-y-3">
              {rentals.map((r) => (
                <div key={r.id} className="border border-neutral-200 p-4">
                  <div className="flex justify-between mb-2">
                    <div className="font-bold">{r.bike_name}</div>
                    <div className="overline">{r.status}</div>
                  </div>
                  <div className="text-xs text-neutral-500 mb-2">{r.start_date?.slice(0, 10)} → {r.end_date?.slice(0, 10)}</div>
                  <div className="text-sm">€{r.total.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
